import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Edit3, Plus, Trash2, RefreshCw, MessageCircle, Store, MapPin } from "lucide-react";
import { TransferDialog } from "../components/TransferDialog";
import { useBranches, branchName } from "../lib/branches";
import ShareCarDialog from "../components/ShareCarDialog";
import MarketplaceDialog from "../components/MarketplaceDialog";
import { api, formatApiError } from "../lib/api";
import { useSettings, formatMoney, formatNumber } from "../lib/settings";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import StatusBadge from "../components/StatusBadge";
import { CostsTab, PhotosTab, DocumentsTab, ActivityTab, ManagersOnly } from "../components/VehicleTabs";
import { ConsignmentPanel } from "../components/ConsignmentPanel";
import { DepositRulePanel } from "../components/DepositRulePanel";

import { tr, lbl, LANG } from "../lib/i18n";
const STATUSES = [
  "Available", "Reserved", "Sold", "Under Preparation", "Under Maintenance",
  "Consignment", "Out for Inspection", "On Test Drive", "Returned to Owner",
];
import SocialPostsTab from "../components/SocialPostsTab";
const TABS = [
  ["overview", tr("x.overview")], ["costs", tr("x.costs_profit")], ["photos", tr("x.photos")],
  ["documents", tr("x.documents")], ["social", tr("sp.tab")], ["activity", tr("x.activity")],
];

export default function VehicleDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { settings } = useSettings();
  const { user, can } = useAuth();
  const [v, setV] = useState(null);
  const [tab, setTab] = useState("overview");
  const [heroIdx, setHeroIdx] = useState(0);
  const [statusOpen, setStatusOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const { multi, branches, all } = useBranches();
  const [shareOpen, setShareOpen] = useState(null);

  const canSeeMoney = ["admin", "accounts", "management"].includes(user?.role);
  const canEdit = can("vehicles", "edit");

  const load = async () => {
    try { const { data } = await api.get(`/vehicles/${id}`); setV(data); } catch (e) { if (e?.response?.status === 404) setV(false); else toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const photos = v?.photos || [];
  const hero = photos[heroIdx]?.url;
  const thumbs = photos.slice(0, 4);

  const togglePublish = async () => {
    try {
      await api.patch(`/vehicles/${id}`, { publish_to_website: !v.publish_to_website });
      toast.success(!v.publish_to_website ? tr("x.published_to_website") : tr("x.hidden_from_website"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const extraCosts = (v?.cost_lines || []).reduce((s, c) => s + Number(c.amount || 0), 0);
  const expectedMargin = useMemo(() => {
    if (!v) return null;
    return (v.asking_price || 0) - (v.purchase_price || 0) - extraCosts;
  }, [v, extraCosts]);

  if (v === false) return <div className="py-10 text-center" data-testid="vehicle-not-found">{tr("br.car_not_found")}</div>;
  if (!v) return <div className="py-10 text-center opacity-60">{tr("x.loading")}</div>;

  const deleteVehicle = async () => {
    if (!window.confirm(tr("x.delete_this_cannot_be_undone", { p0: v.stock_number }))) return;
    try { await api.delete(`/vehicles/${id}`); toast.success(tr("x.vehicle_deleted")); nav("/inventory"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="vehicle-detail">
      <button onClick={() => nav("/inventory")} className="btn-ghost mb-3" data-testid="back-to-inventory">
        <ArrowLeft size={14} strokeWidth={1.75} className="flip-rtl" /> {tr("x.inventory")}
      </button>

      <div className="flex items-end justify-between gap-3 flex-wrap mb-6">
        <div>
          <div className="kicker">{v.stock_number} · {v.model_year}{v.specification ? ` · ${lbl(v.specification)}` : ""}</div>
          <h1 className="mt-2 flex items-center gap-3" style={{ fontSize: 34, fontWeight: 600, letterSpacing: "-0.02em" }}>
            {v.make} {v.model}
            <StatusBadge status={v.status} />
          </h1>
          {multi && <div className="text-sm mt-2 inline-flex items-center gap-1" data-testid="vehicle-branch"><MapPin size={14} strokeWidth={1.75} />{branchName(all.find((b) => b.id === v.branch_id) || all.find((b) => b.is_main), LANG)}</div>}
        </div>
      </div>

      {/* Two-column panel */}
      <div
        className="vehicle-profile-grid gap-[2px]"
        style={{ background: "var(--divider)", border: "1px solid var(--divider)" }}
      >
        {/* LEFT: gallery */}
        <div style={{ background: "#fff", padding: 20, minWidth: 0 }}>
          <div style={{ aspectRatio: "16/9", background: "var(--surface)", overflow: "hidden" }}>
            {hero ? <img src={hero} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div className="h-full flex items-center justify-center text-sm" style={{ color: "var(--text-muted)" }} data-testid="vehicle-no-photo">{tr("x.no_photo")}</div>}
          </div>
          {thumbs.length > 1 && <div className="mt-3 grid gap-[2px]" style={{ gridTemplateColumns: "repeat(4, minmax(0,1fr))" }}>
            {thumbs.map((p, i) => (
              <button key={i} onClick={() => setHeroIdx(i)} data-testid={`vehicle-thumb-${i}`}
                style={{ aspectRatio: "4/3", background: "#fff", border: heroIdx === i ? "2px solid var(--accent)" : "0", padding: 0 }}>
                <img src={p.variants?.thumbnail || p.thumbnail_url || p.url} alt="" loading="lazy" decoding="async" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </button>
            ))}
          </div>}
        </div>

        {/* RIGHT: facts + ledger + actions */}
        <div style={{ background: "#fff", padding: 24, minWidth: 0 }}>
          <div className="rule-top pt-4 grid gap-5" style={{ gridTemplateColumns: "repeat(2, minmax(0,1fr))" }}>
            <Fact label={tr("x.asking_price")} value={formatMoney(v.asking_price || 0, settings)} />
            <Fact label={tr("x.days_in_stock")} value={formatNumber(v.days_in_stock || 0)} />
            <Fact label={tr("x.mileage")} value={`${formatNumber(v.mileage || 0)} ${tr("x.km_unit")}`} />
            <Fact label={tr("x.vin_label")} value={v.vin || "—"} tight wide />
          </div>

          {canSeeMoney && v.consignment_money && <ConsignmentLedger m={v.consignment_money} settings={settings} />}
          {canSeeMoney && !v.consignment_money && (
            <div className="mt-6">
              <div className="kicker mb-2">{tr("x.pricing_ownership")}</div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <tbody>
                  <Ledger label={tr("x.purchase")} value={formatMoney(v.purchase_price || 0, settings)} />
                  <Ledger label={tr("x.additional_costs")} value={formatMoney(extraCosts, settings)} />
                  <Ledger label={tr("x.minimum")} value={formatMoney(v.minimum_selling_price || 0, settings)} />
                  <Ledger label={tr("x.asking")} value={formatMoney(v.asking_price || 0, settings)} ruledUnder />
                  <Ledger label={tr("x.expected_margin")} value={formatMoney(expectedMargin || 0, settings)} accent />
                </tbody>
              </table>
            </div>
          )}

          {v.ownership_type === "Customer Consignment" && can("consignment", "view") && <ConsignmentPanel vehicleId={v.id} settings={settings} />}

          <label className="mt-6 flex items-center gap-3 cursor-pointer" style={{ borderTop: "1px solid var(--divider-soft)", paddingTop: 16 }}>
            <input type="checkbox" checked={!!v.publish_to_website} onChange={togglePublish} disabled={!canEdit}
              style={{ width: 20, height: 20, accentColor: "var(--accent)", borderRadius: 0 }} data-testid="toggle-publish" />
            <span className="text-sm font-medium">{tr("x.show_on_website")}</span>
          </label>

          <div className="mt-5 flex gap-2 flex-wrap items-center">
            {["admin", "management", "sales"].includes(user?.role) && (
              <>
                <button className="btn-secondary" onClick={() => setShareOpen("wa")} data-testid="share-car-btn">
                  <MessageCircle size={14} strokeWidth={1.75} /> {tr("share.whatsapp_short")}
                </button>
                <button className="btn-secondary" onClick={() => setShareOpen("mp")} data-testid="marketplace-btn">
                  <Store size={14} strokeWidth={1.75} /> {tr("share.mp_button")}
                </button>
                <ShareCarDialog vehicleId={id} open={shareOpen === "wa"} onClose={() => setShareOpen(null)} />
                <MarketplaceDialog vehicleId={id} stock={v.stock_number} open={shareOpen === "mp"} onClose={() => setShareOpen(null)} />
              </>
            )}
            {canEdit && (
              <>
                <button className="btn-secondary" onClick={() => nav(`/inventory/${id}/edit`)} data-testid="edit-vehicle">
                  <Edit3 size={14} strokeWidth={1.75} /> {tr("x.edit_vehicle")}
                </button>
                <button className="btn-secondary" onClick={() => setStatusOpen(true)} data-testid="change-status-btn">
                  <RefreshCw size={14} strokeWidth={1.75} /> {tr("x.change_status")}
                </button>
                {multi && branches.length > 1 && (
                  <button className="btn-secondary" onClick={() => setTransferOpen(true)} data-testid="transfer-btn">
                    <MapPin size={14} strokeWidth={1.75} /> {tr("br.transfer")}
                  </button>
                )}
              </>
            )}
            {can("vehicles", "delete") && (
              <button className="btn-ghost" style={{ color: "var(--status-overdue)" }} onClick={deleteVehicle} data-testid="delete-vehicle-btn">
                <Trash2 size={14} strokeWidth={1.75} /> {tr("x.delete")}
              </button>
            )}
            {v.status === "available" && can("sales", "create") && (
              <button className="btn-primary" onClick={() => nav(`/sales/new?vehicle=${id}`)} data-testid="create-sale">
                <Plus size={14} strokeWidth={1.75} /> {tr("x.create_sale")}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="underline-tabs mt-10">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} className={tab === k ? "active" : ""} data-testid={`tab-${k}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div
          className="mt-6 grid gap-[2px]"
          style={{
            background: "var(--divider)",
            border: "1px solid var(--divider)",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%,340px), minmax(0,1fr)))"
          }}
        >
          <DetailList rows={[
            [tr("x.make_2"), v.make], [tr("x.model"), v.model], [tr("x.year"), v.model_year], [tr("x.trim"), v.trim],
            [tr("x.specification"), lbl(v.specification)], [tr("x.body_type"), lbl(v.body_type)],
          ]} />
          <DetailList rows={[
            [tr("x.exterior_colour"), v.exterior_color], [tr("x.interior_colour"), v.interior_color],
            [tr("x.transmission"), lbl(v.transmission)], [tr("x.fuel_type"), lbl(v.fuel_type)],
            [tr("x.engine"), v.engine], [tr("x.cylinders"), v.cylinders],
          ]} />
        </div>
      )}
      {tab === "overview" && v.publish_to_website && user?.role !== "viewer" && <DepositRulePanel vehicleId={v.id} canEdit={can("vehicles", "approve")} />}

      {tab === "costs" && (canSeeMoney ? <CostsTab v={v} settings={settings} canEdit={canEdit} reload={load} /> : <ManagersOnly />)}
      {tab === "photos" && <PhotosTab v={v} canEdit={canEdit} reload={load} />}
      {tab === "documents" && <DocumentsTab v={v} onChange={setV} />}
      {tab === "social" && <SocialPostsTab vehicleId={v.id} />}
      {tab === "activity" && <ActivityTab v={v} />}
      {statusOpen && <StatusModal id={id} current={v.status} onClose={() => setStatusOpen(false)} onSaved={() => { setStatusOpen(false); load(); }} />}
      {transferOpen && <TransferDialog vehicle={v} onClose={() => setTransferOpen(false)} onDone={() => { setTransferOpen(false); load(); }} />}
    </div>
  );
}

function StatusModal({ id, current, onClose, onSaved }) {
  const [status, setStatus] = useState(current);
  const [reason, setReason] = useState("");
  const save = async () => {
    try { await api.post(`/vehicles/${id}/status`, { status, reason: reason || null }); toast.success(tr("x.status_updated")); onSaved(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="panel w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="status-modal">
        <div className="panel-title"><div className="kicker">{tr("x.change_status")}</div></div>
        <div className="panel-body grid gap-3">
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="input" data-testid="status-select">
            {STATUSES.map((s) => <option key={s} value={s}>{lbl(s)}</option>)}
          </select>
          <textarea rows={3} placeholder={tr("x.reason_optional")} value={reason} onChange={(e) => setReason(e.target.value)} className="input" style={{ height: "auto", padding: 10 }} data-testid="status-reason" />
          <div className="flex gap-2 justify-end">
            <button className="btn-secondary" onClick={onClose} data-testid="status-cancel">{tr("x.cancel")}</button>
            <button className="btn-primary" onClick={save} data-testid="submit-status-change">{tr("x.update")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Fact({ label, value, tight, wide }) {
  return (
    <div style={wide ? { gridColumn: "1 / -1" } : undefined}>
      <div className="kpi-label">{label}</div>
      <div className="mt-1" style={{ fontSize: "clamp(14px, 4vw, 18px)", fontWeight: 600, whiteSpace: tight ? "normal" : "nowrap", wordBreak: tight ? "break-all" : "normal", overflow: "hidden", textOverflow: "ellipsis" }}>
        <bdi>{value}</bdi>
      </div>
    </div>
  );
}
function Ledger({ label, value, ruledUnder, accent }) {
  return (
    <tr style={{ borderBottom: ruledUnder ? "2px solid var(--text)" : "1px solid var(--divider-soft)" }}>
      <td style={{ padding: "10px 0", fontSize: 13, color: "var(--text-muted)" }}>{label}</td>
      <td className="tabular" style={{ padding: "10px 0", textAlign: "end", fontSize: 14, fontWeight: 500, color: accent ? "var(--accent-700)" : "inherit" }}>{value}</td>
    </tr>
  );
}
function ConsignmentLedger({ m, settings }) {
  const money = (x) => (x == null ? "—" : formatMoney(x, settings));
  const notSet = <span className="text-red-600" data-testid="ledger-commission-not-set">{tr("x.commission_not_set")}</span>;
  const na = <span className="opacity-60">{tr("x.not_applicable")}</span>;
  return (
    <div className="mt-6" data-testid="consignment-ledger">
      <div className="kicker mb-2">{tr("x.pricing_ownership")}</div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          <Ledger label={tr("x.owner_price")} value={<span data-testid="ledger-owner-price">{money(m.owner_price)}</span>} />
          <Ledger label={tr("x.asking")} value={<span data-testid="ledger-asking">{money(m.asking_price)}</span>} />
          <Ledger label={tr("x.commission_estimate")} value={m.commission_set ? <span data-testid="ledger-commission">{money(m.commission_estimate)}</span> : notSet} />
          <Ledger label={tr("x.owner_payout_estimate")} value={<span data-testid="ledger-owner-payout">{money(m.owner_payout_estimate)}</span>} />
          <Ledger label={tr("x.purchase")} value={na} />
          <Ledger label={tr("x.additional_costs")} value={na} ruledUnder />
          <Ledger label={tr("x.expected_margin")} value={<span data-testid="ledger-expected-margin">{m.commission_set ? money(m.expected_margin) : notSet}</span>} accent />
        </tbody>
      </table>
    </div>
  );
}

function DetailList({ rows }) {
  return (
    <div style={{ background: "#fff", padding: 20 }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          {rows.filter(([, v]) => v).map(([k, val]) => (
            <tr key={k} style={{ borderBottom: "1px solid var(--divider-soft)" }}>
              <td style={{ padding: "10px 0", fontSize: 12, color: "var(--text-muted)", width: "42%" }}>{k}</td>
              <td style={{ padding: "10px 0", fontSize: 14, fontWeight: 500 }}>{val}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
